import { useMutation, useQueryClient } from "@tanstack/react-query";
import { updateStoryMeta } from "../services/dataStories/storiesApi";
import { getApiErrorMessage } from "../services/apiErrors";
import { queryKeys } from "../services/queryKeys";
import { useCanvasHistory } from "../contexts/CanvasHistoryContext";
import { renameStoryEntry } from "../services/storyCanvas/historyInverses";
import type { StoryMetaApi } from "../services/dataStories/storiesApi.types";

const RENAME_ERROR_FALLBACK = "Couldn't rename the story. Please try again.";

interface TitleMutationContext {
  previous?: StoryMetaApi;
}

/**
 * Mutation hook for the story title rename, optimistic on the story row
 * (`storyMeta.detail`: StoryLayout's outlet context, the header breadcrumb and
 * the canvas title all read it) and rolled back if the PATCH fails.
 * `saveTitle` ignores calls while a save is already in flight (no concurrent
 * last-write-wins races). Surface `errorMessage` in a Toast and call
 * `clearError` when it closes.
 */
export const useUpdateStoryTitle = (
  workspaceId: string | undefined,
  storyId: string | undefined,
) => {
  const queryClient = useQueryClient();
  const { record } = useCanvasHistory();
  const metaKey = queryKeys.storyMeta.detail(workspaceId ?? "", storyId ?? "");

  const mutation = useMutation<StoryMetaApi, Error, string, TitleMutationContext>({
    mutationFn: (title) => updateStoryMeta(workspaceId!, storyId!, { title }),
    onMutate: async (title) => {
      // Stop an in-flight refetch (build-page polling, window focus) from
      // landing the old title over the optimistic one.
      await queryClient.cancelQueries({ queryKey: metaKey });
      const previous = queryClient.getQueryData<StoryMetaApi>(metaKey);
      // Writes only when the cache already holds the story; never seeds a partial row.
      queryClient.setQueryData<StoryMetaApi>(metaKey, (current) =>
        current ? { ...current, title } : current,
      );
      return { previous };
    },
    onError: (_error, _title, context) => {
      if (context?.previous) {
        queryClient.setQueryData(metaKey, context.previous);
      }
    },
    onSuccess: (meta, title, context) => {
      queryClient.setQueryData(metaKey, meta);
      const previousTitle = context?.previous?.title;
      if (previousTitle !== undefined && previousTitle !== meta.title) {
        record(
          renameStoryEntry(
            { queryClient, workspaceId: workspaceId ?? "", storyId: storyId ?? "" },
            previousTitle,
            meta.title,
          ),
        );
      }
    },
  });

  const saveTitle = (title: string) => {
    if (mutation.isPending) return;
    mutation.mutate(title);
  };

  return {
    saveTitle,
    isSaving: mutation.isPending,
    isError: mutation.isError,
    errorMessage: mutation.isError
      ? getApiErrorMessage(mutation.error, RENAME_ERROR_FALLBACK)
      : null,
    clearError: mutation.reset,
  };
};
